import Card from '@/global/components/Card'
import { TextField } from '@/global/components/TextField'
import { Label } from '@/global/components/ui/label'
import { cn } from '@/lib/utils'
import IconButton from 'global/components/Button/IconButton'
import Dialog from 'global/guides/Dialog'
import { ArrowLeftIcon, SearchIcon } from 'lucide-react'
import { type ComponentType, type ReactNode, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { type ZodTypeAny, z } from 'zod'
import { agruparCatalogo } from './catalogo'

export type Icone = ComponentType<{ className?: string }>

/** Cor do selo: `warning` nas condições ("Se"), `info` nas ações ("Então"). */
export type CorSelo = 'warning' | 'info'

/** Fundo e texto do selo; reusado nos rótulos Se/Então do histórico. */
export const CLASSES_COR_SELO: Record<CorSelo, string> = {
    warning: 'bg-warning/15 text-warning',
    info: 'bg-info/15 text-info',
}

interface ItemCatalogo {
    label: string
    descricao: string
    grupo: string
    /** Motivo de estar desligado; presente, aparece esmaecido e não pode ser escolhido. */
    indisponivel?: string
}

export function SeloIcone({
    icone: Icone,
    cor,
}: {
    icone: Icone
    cor: CorSelo
}) {
    return (
        <span
            className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md',
                CLASSES_COR_SELO[cor],
            )}
        >
            <Icone className="size-5" />
        </span>
    )
}

interface CatalogoDialogProps<TId extends string> {
    titulo: string
    /** Substantivo da busca e do vazio: "condição", "ação". */
    item: string
    cor: CorSelo
    catalogo: Record<TId, ItemCatalogo>
    /** Registro da tela de cada id; o selo usa o `icone`. */
    ui: Record<TId, { icone: Icone }>
    /** Campo do formulário que guarda o id escolhido. */
    name: string
    /** Valores do formulário ao trocar para `id`; descartam os da escolha anterior. */
    iniciar: (id: TId) => Record<string, unknown>
    schema: ZodTypeAny
    /** Valores em edição; ausente, o diálogo abre no catálogo. */
    valor?: Record<string, unknown>
    onClose: () => void
    onSubmit: (valores: z.infer<ZodTypeAny>) => void
    /** Campos do passo de configuração, abaixo do item escolhido. */
    children: ReactNode
}

/**
 * Sub-diálogo de duas etapas: catálogo pesquisável em grupos e, ao clicar num
 * item, a configuração dele. Tem formulário próprio, separado do da rotina.
 */
export function CatalogoDialog<TId extends string>({
    titulo,
    item,
    cor,
    catalogo,
    ui,
    name,
    iniciar,
    schema,
    valor,
    onClose,
    onSubmit,
    children,
}: CatalogoDialogProps<TId>) {
    const [isConfigurando, setIsConfigurando] = useState(!!valor)

    return (
        <Dialog.Root
            open
            width="xl"
            shouldTransformarValores={false}
            onClose={onClose}
            schema={schema}
            defaultValues={valor ?? {}}
            onSubmit={onSubmit}
        >
            <Dialog.Title>
                <span className="flex items-center gap-2">
                    {isConfigurando && (
                        <IconButton
                            title="Voltar ao catálogo"
                            onClick={() => setIsConfigurando(false)}
                        >
                            <ArrowLeftIcon />
                        </IconButton>
                    )}
                    {titulo}
                </span>
            </Dialog.Title>
            <Dialog.Content>
                {isConfigurando ? (
                    <>
                        <ItemEscolhido
                            name={name}
                            cor={cor}
                            catalogo={catalogo}
                            ui={ui}
                        />
                        {children}
                    </>
                ) : (
                    <Catalogo
                        item={item}
                        cor={cor}
                        catalogo={catalogo}
                        ui={ui}
                        name={name}
                        iniciar={iniciar}
                        onEscolhido={() => setIsConfigurando(true)}
                    />
                )}
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {isConfigurando && (
                    <Dialog.Actions.PrimaryButton>
                        Aplicar
                    </Dialog.Actions.PrimaryButton>
                )}
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function Catalogo<TId extends string>({
    item,
    cor,
    catalogo,
    ui,
    name,
    iniciar,
    onEscolhido,
}: Pick<
    CatalogoDialogProps<TId>,
    'item' | 'cor' | 'catalogo' | 'ui' | 'name' | 'iniciar'
> & { onEscolhido: () => void }) {
    const { setValue, getValues } = useFormContext()
    const escolhido = useWatch({ name }) as TId | undefined
    const [busca, setBusca] = useState('')
    const grupos = agruparCatalogo(catalogo, busca)

    const escolher = (id: TId) => {
        if (id !== getValues(name))
            for (const [campo, valor] of Object.entries(iniciar(id)))
                setValue(campo, valor)
        onEscolhido()
    }

    return (
        <div className="flex flex-col gap-4">
            <TextField
                autoFocus
                unHelperText
                autoComplete="off"
                placeholder={`Buscar ${item}`}
                startAdornment={<SearchIcon className="opacity-75 mb-1" />}
                value={busca}
                onChange={(evento: React.ChangeEvent<HTMLInputElement>) =>
                    setBusca(evento.target.value)
                }
            />
            {grupos.length === 0 && (
                <p className="text-sm text-muted-foreground">
                    Nenhuma {item} encontrada
                </p>
            )}
            {grupos.map(([grupo, ids]) => (
                <section key={grupo} className="flex flex-col gap-2">
                    <Label className="uppercase text-muted-foreground">
                        {grupo}
                    </Label>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {ids.map(id => (
                            <Card
                                key={id}
                                role="radio"
                                aria-checked={id === escolhido}
                                aria-disabled={!!catalogo[id].indisponivel}
                                title={catalogo[id].indisponivel}
                                onClick={
                                    catalogo[id].indisponivel
                                        ? undefined
                                        : () => escolher(id)
                                }
                                className={cn(
                                    'flex select-none items-center gap-3 p-3',
                                    catalogo[id].indisponivel
                                        ? 'cursor-not-allowed opacity-disabled'
                                        : 'cursor-pointer transition-colors duration-200 hover:bg-primary/10',
                                    id === escolhido &&
                                        'ring-2 ring-primary shadow-lg',
                                )}
                            >
                                <SeloIcone icone={ui[id].icone} cor={cor} />
                                <span className="min-w-0">
                                    <span className="block font-semibold">
                                        {catalogo[id].label}
                                    </span>
                                    <span
                                        title={catalogo[id].descricao}
                                        className="block truncate text-sm text-muted-foreground mt-1"
                                    >
                                        {catalogo[id].descricao}
                                    </span>
                                </span>
                            </Card>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}

function ItemEscolhido<TId extends string>({
    name,
    cor,
    catalogo,
    ui,
}: Pick<CatalogoDialogProps<TId>, 'name' | 'cor' | 'catalogo' | 'ui'>) {
    const id = useWatch({ name }) as TId

    return (
        <div className="flex items-center gap-3 pb-4">
            <SeloIcone icone={ui[id].icone} cor={cor} />
            <span>
                <span className="block font-semibold">
                    {catalogo[id].label}
                </span>
                <span className="block text-sm text-muted-foreground">
                    {catalogo[id].descricao}
                </span>
            </span>
        </div>
    )
}
